import { GetPaymentOptionsViewModel } from '@/types/api'
import { UserType } from '@/types/pam-api'

import { CartActionPlacement } from '@/features/webengage/types'

export type WindowWithDataLayer<T extends EventsData['event']> = Window & {
    dataLayer?: Array<
        {
            event: T
        } & EventDataMap[T]
    >
}

export enum SupportedEvents {
    click = 'click',
    pageview = 'pageview',
    purchase = 'purchase',
    add_payment_info = 'add_payment_info',
    begin_checkout = 'begin_checkout',
    add_shipping_info = 'add_shipping_info',
    location_not_detected = 'location_not_detected',
    user_attributes = 'user_attributes',
    user_id = 'user_id',
    popUp = 'popUp',
    add_to_cart = 'add_to_cart',
    remove_from_cart = 'remove_from_cart',
    view_item = 'view_item',
    view_item_list = 'view_item_list',
    view_cart = 'view_cart',
    cta_click = 'cta_click',
    login = 'login',
    sign_up = 'sign_up',
}

export type DataLayerErrorHandlerCallback = () => void

interface BaseEvent<T extends SupportedEvents> {
    event: T
}

interface ClickEventData {
    eventCategory: string
    eventAction: string | null
}

interface PageViewEventData {
    page: string
}

interface UserAttributesData {
    data: {
        logged_in: 'true' | 'false'
        user_id: string | null
        aov: number | null
        user_type: Lowercase<UserType> | null
        total_order: number
        last_purchase: number | null
        city: string | null
        province: string | null
    }
}

interface UserIdData {
    user_id: string | null
}

interface PopUpData {
    elementId: number | null
    action: PopupAction
    page_path: string
}

export interface AddOrRemoveCartData {
    ecommerce: {
        currency: 'USD'
        value: number | null
        cart_id: string | null
        items: Array<EventsCartItemData>
    }
}

interface ViewItemData {
    ecommerce: {
        value: number
        currency: 'USD'
        items: {
            item_id: string
            item_name: string
            discount: number
            coupon: null
            item_brand: string
            item_category: string | null
            item_category2: string | null
            item_category3: string | null
            item_category4: string | null
            item_category5: string | null
            item_variant: string | null
            price: number
            quantity: 1
            item_list_id: ViewItemType
            item_list_name: ViewItemType
            index: null
            ship_by_seller: boolean
        }[]
    }
}

interface ViewItemListData {
    ecommerce: {
        items: Array<{
            item_name: string | null
            item_id: string | null
            item_brand: string | null
            item_category3: string
            item_category2: string
            item_category: string
            price: number | null
            index: number
            pageNumber: number
        }>
    }
}

export interface EventsCartItemData {
    item_id: string | null
    item_name: string | null
    coupon: 'pricinggift' | null
    item_brand: string | null
    item_category: string | null
    item_category2: string | null
    item_category3: string | null
    item_category4: string | null
    item_category5: string | null
    item_variant: string | null
    price: number
    discount: number
    quantity: number
    index?: number
    item_list_id?: CartActionPlacement
    item_list_name?: CartActionPlacement
    ship_by_seller: boolean
}

interface ViewCartData {
    ecommerce: {
        currency: 'USD'
        value: number | null
        cart_id: string | null
        items: Array<EventsCartItemData>
    }
}

interface BeginCheckoutData {
    ecommerce: {
        currency: 'USD'
        value: number
        coupon: string | null
        coupon_amount: number | null
        cart_id: string | null
        items: Array<EventsCartItemData>
    }
}

interface AddShippingInfoData {
    ecommerce: {
        currency: 'USD'
        value: number
        coupon: string | null
        coupon_amount: number | null
        shipping_tier: string | null
        shipment_count: number | null
        cart_id: string | null
        items: Array<EventsCartItemData>
    }
}

interface AddPaymentInfoData {
    ecommerce: {
        currency: 'USD'
        value: number
        coupon: string | null
        coupon_amount: number | null
        payment_type: GetPaymentOptionsViewModel['paymentMethodType']
        cart_id: string | null
        items: Array<EventsCartItemData>
    }
}

export enum StoryCtaElementTypes {
    Story = 'Story',
    StoryPage = 'StoryPage',
}

export interface RecommendedProductCtaClickData {
    elementType: 'AddToCart-ProductList' | 'PDP-ProductList'
    elementName: 'AddToCart-ProductList' | 'PDP-complementary' | 'PDP-similar' | 'PDP-also-bought' | 'PDP-sponsored-ads'
    elementIndex: number
    destinationUrl: string
    ImageUrl: string
    page_path: string
    item_id: string
    item_name: string
    item_brand: string
    item_category: string
    item_category2: string
    item_category3: string
    item_category4: string | null
    item_category5: string | null
    price: number
    discount: number
    user_id: string
}

export type RecommendedProductCtaClickParams = Omit<
    RecommendedProductCtaClickData,
    'page_path' | 'destinationUrl' | 'eventName'
> & {
    slug: string
}

export interface CtaClickEventData {
    elementType: string
    elementIndex: number
    itemID: string
    elementName?: string
    destinationUrl?: string | null
    componentId?: string | null
    ImageUrl?: string | null
    elementGroupName?: string
    term?: string
    page_path?: string
}

interface PurchaseEventData {
    ecommerce: {
        currency: 'USD'
        value: number
        coupon: string | null
        coupon_amount: number | null
        shipping: number | null
        transaction_id: string
        order_id: string | null
        cart_id: string | null
        shipping_tier: string | null
        shipment_count: number | null
        payment_type: string | null
        order_count: number | null
        items: Array<EventsCartItemData>
    }
}

interface LoginEventData {
    method: 'phonenumber'
}

interface SignupEventData {
    method: 'phonenumber'
}

interface LoginEvent extends BaseEvent<SupportedEvents.login>, LoginEventData {}

interface SignupEvent extends BaseEvent<SupportedEvents.sign_up>, SignupEventData {}

interface ClickEvent extends BaseEvent<SupportedEvents.click>, ClickEventData {}

interface PageViewEvent extends BaseEvent<SupportedEvents.pageview>, PageViewEventData {}

interface PurchaseEvent extends BaseEvent<SupportedEvents.purchase>, PurchaseEventData {}

interface BeginCheckoutEvent extends BaseEvent<SupportedEvents.begin_checkout>, BeginCheckoutData {}

type LocationNotDetectedEvent = BaseEvent<SupportedEvents.location_not_detected>

interface UserAttributesEvent extends BaseEvent<SupportedEvents.user_attributes>, UserAttributesData {}

interface UserIdEvent extends BaseEvent<SupportedEvents.user_id>, UserIdData {}

interface PopUpEvent extends BaseEvent<SupportedEvents.popUp>, PopUpData {}

interface AddToCartEvent extends BaseEvent<SupportedEvents.add_to_cart>, AddOrRemoveCartData {}

interface RemoveFromCartEvent extends BaseEvent<SupportedEvents.remove_from_cart>, AddOrRemoveCartData {}

interface ViewItemEvent extends BaseEvent<SupportedEvents.view_item>, ViewItemData {}

interface ViewItemListEvent extends BaseEvent<SupportedEvents.view_item_list>, ViewItemListData {}

interface ViewCartEvent extends BaseEvent<SupportedEvents.view_cart>, ViewCartData {}

interface AddShippingInfoEvent extends BaseEvent<SupportedEvents.add_shipping_info>, AddShippingInfoData {}

interface AddPaymentInfoEvent extends BaseEvent<SupportedEvents.add_payment_info>, AddPaymentInfoData {}

interface CtaClickEvent extends BaseEvent<SupportedEvents.cta_click>, CtaClickEventData {}

export interface RecommendedProductCtaClickEvent
    extends BaseEvent<SupportedEvents.cta_click>,
        RecommendedProductCtaClickData {}

export type EventsData =
    | ClickEvent
    | PageViewEvent
    | PurchaseEvent
    | BeginCheckoutEvent
    | LocationNotDetectedEvent
    | UserAttributesEvent
    | UserIdEvent
    | PopUpEvent
    | AddToCartEvent
    | RemoveFromCartEvent
    | ViewItemEvent
    | ViewItemListEvent
    | ViewCartEvent
    | AddPaymentInfoEvent
    | AddShippingInfoEvent
    | RecommendedProductCtaClickEvent
    | LoginEvent
    | SignupEvent
    | CtaClickEvent

export type EventDataMap = {
    click: ClickEventData
    pageview: PageViewEventData
    purchase: PurchaseEventData
    begin_checkout: BeginCheckoutData
    location_not_detected: undefined
    user_attributes: UserAttributesData
    user_id: UserIdData
    popUp: PopUpData
    add_to_cart: AddOrRemoveCartData
    remove_from_cart: AddOrRemoveCartData
    view_item: ViewItemData
    view_item_list: ViewItemListData
    view_cart: ViewCartData
    add_shipping_info: AddShippingInfoData
    add_payment_info: AddPaymentInfoData
    cta_click: CtaClickEventData | RecommendedProductCtaClickData
    login: LoginEventData
    sign_up: SignupEventData
}

export enum PopupAction {
    View = 'view',
    Click = 'click',
    Copy = 'copy',
}

export enum ViewItemType {
    Video = 'video',
    PDP = 'pdp',
}
